import { Identifier } from '@okletsplay/core';
import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { cancelationSubscribe } from 'app/services/api/utils';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import {
  MarkUserAsReadyRequest,
  UpdateTournamentAssetsRequest,
} from 'modules/tournaments/interfaces';
import {
  UpdateTournamentTeamRequest,
  UpdateTournamentInvitationStatusRequest,
  DisqualifyTeamRequest,
  UpdateTournamentSpectatorStatusRequest,
  RunBracketMatchRequest,
  CreateTournamentTeamRequest,
} from 'modules/tournaments/store/current-tournament/interfaces';
import { UpdateMatchGameResultsRequest } from 'modules/tournaments/store/current-tournament/interfaces/update-match-game-results.request';
import { CurrentTournamentState } from './interfaces';
import { CurrentTournamentStoreStatus } from './enum';
import { LolTournamentEntity, TournamentEntity } from 'app/interfaces/entity';
import { getTournamentActivity } from 'modules/tournament-activity/store/repository';

export const getTournamentById = createAsyncThunk(
  `${EntityStoreName.Tournaments}/getTournamentById`,
  (id: Identifier, { signal }): Promise<TournamentEntity> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<TournamentEntity>({
      params: {
        method: 'GET',
        resource: ApiResource.Tournaments,
        urlPrefix: id,
        cancelToken,
      },
    });
  }
);

export const cancelTournament = createAsyncThunk(
  `${EntityStoreName.Tournaments}/cancelTournament`,
  (id: Identifier, { signal }): Promise<TournamentEntity> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<TournamentEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Tournaments,
        urlPrefix: `${id}/cancel`,
        cancelToken,
      },
    });
  }
);

export const updateTournament = createAsyncThunk(
  `${EntityStoreName.Tournaments}/updateTournament`,
  (id: Identifier): Promise<TournamentEntity> => {
    return apiRequestCall<TournamentEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Tournaments,
        urlPrefix: `${id}/win-animation`,
      },
    });
  }
);

export const getTournamentGameUrl = createAsyncThunk(
  `${EntityStoreName.Tournaments}/getTournamentGameUrl`,
  (id: Identifier, { signal }): Promise<string> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<string>({
      params: {
        method: 'GET',
        resource: ApiResource.Tournaments,
        urlPrefix: `${id}/game-url`,
        cancelToken,
      },
    });
  }
);

export const joinTournament = createAsyncThunk(
  `${EntityStoreName.Tournaments}/joinTournament`,
  async (
    { identifier, data }: CreateTournamentTeamRequest,
    { signal, dispatch }
  ): Promise<unknown> => {
    const cancelToken = cancelationSubscribe(signal);

    const result = await apiRequestCall({
      params: {
        method: 'POST',
        resource: ApiResource.Tournaments,
        urlPrefix: `${identifier}/join`,
        cancelToken,
        data,
      },
      successRequestMessage: data ? 'Join request has been sent.' : '',
    });

    dispatch(getTournamentActivity());

    return result;
  }
);

export const updateTournamentTeam = createAsyncThunk(
  `${EntityStoreName.Tournaments}/joinTournament`,
  ({ identifier, teamId, data }: UpdateTournamentTeamRequest, { signal }): Promise<unknown> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall({
      params: {
        method: 'PUT',
        resource: ApiResource.Tournaments,
        urlPrefix: `${identifier}/teams/${teamId}`,
        cancelToken,
        data,
      },
      successRequestMessage: 'Team updated successfully',
    });
  }
);

export const updateInvitationStatus = createAsyncThunk(
  `${EntityStoreName.Tournaments}/updateInvitationStatus`,
  async (
    { identifier, data }: UpdateTournamentInvitationStatusRequest,
    { signal, dispatch }
  ): Promise<TournamentEntity> => {
    const cancelToken = cancelationSubscribe(signal);

    const result = await apiRequestCall<TournamentEntity>({
      params: {
        method: 'PUT',
        resource: ApiResource.Tournaments,
        urlPrefix: `${identifier}/invitation-status`,
        cancelToken,
        data,
      },
      successRequestMessage: 'Invitation status has been updated.',
    });

    dispatch(getTournamentActivity());

    return result;
  }
);

export const updateSpectatorStatus = createAsyncThunk(
  `${EntityStoreName.Tournaments}/updateSpectatorStatus`,
  (
    { tournamentId, spectatorId, data }: UpdateTournamentSpectatorStatusRequest,
    { signal }
  ): Promise<TournamentEntity> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<TournamentEntity>({
      params: {
        method: 'PUT',
        resource: ApiResource.Tournaments,
        urlPrefix: `${tournamentId}/spectators/${spectatorId}`,
        cancelToken,
        data,
      },
      successRequestMessage: 'Invitation status has been updated.',
    });
  }
);

export const withdrawTournament = createAsyncThunk(
  `${EntityStoreName.Tournaments}/withdrawTournament`,
  async (identifier: Identifier, { signal, dispatch }): Promise<unknown> => {
    const cancelToken = cancelationSubscribe(signal);

    const result = await apiRequestCall({
      params: {
        method: 'POST',
        resource: ApiResource.Tournaments,
        urlPrefix: `${identifier}/withdraw`,
        cancelToken,
      },
      successRequestMessage: 'You successfully left the tournament.',
    });

    dispatch(getTournamentActivity());

    return result;
  }
);

export const disqualifyTeam = createAsyncThunk(
  `${EntityStoreName.Tournaments}/disqualifyTeam`,
  (payload: DisqualifyTeamRequest, { signal }): Promise<unknown> => {
    const cancelToken = cancelationSubscribe(signal);
    const { tournamentId, teamId } = payload;

    return apiRequestCall({
      params: {
        method: 'DELETE',
        resource: ApiResource.Tournaments,
        urlPrefix: `${tournamentId}/teams/${teamId}/disqualify`,
        cancelToken,
      },
      successRequestMessage: 'You have disqualified the team successfully.',
    });
  }
);

export const updateMatchGameResults = createAsyncThunk(
  `${EntityStoreName.Tournaments}/updateMatchGameResults`,
  (payload: UpdateMatchGameResultsRequest, { signal }): Promise<unknown> => {
    const cancelToken = cancelationSubscribe(signal);
    const { tournamentId, data } = payload;

    return apiRequestCall({
      params: {
        method: 'POST',
        resource: ApiResource.Tournaments,
        urlPrefix: `${tournamentId}/match-results`,
        data,
        cancelToken,
      },
      successRequestMessage: 'You have updated match results.',
    });
  }
);

export const runBracketMatch = createAsyncThunk(
  `${EntityStoreName.Tournaments}/runBracketMatch`,
  (payload: RunBracketMatchRequest, { signal }): Promise<unknown> => {
    const cancelToken = cancelationSubscribe(signal);
    const { tournamentId, matchId } = payload;

    return apiRequestCall({
      params: {
        method: 'POST',
        resource: ApiResource.Tournaments,
        urlPrefix: `${tournamentId}/matches/${matchId}`,
        cancelToken,
      },
      successRequestMessage: 'You have run the bracket match.',
    });
  }
);

export const updateTournamentAssets = createAsyncThunk(
  `${EntityStoreName.Tournaments}/updateTournamentAssets`,
  (data: UpdateTournamentAssetsRequest): Promise<TournamentEntity> => {
    return apiRequestCall<TournamentEntity>({
      params: {
        method: 'PATCH',
        resource: ApiResource.Tournaments,
        urlPrefix: `${data.tournamentId}`,
        data: data.payload,
      },
      successRequestMessage: 'Tournament assets has been successfully updated.',
    });
  }
);

export const markAsReady = createAsyncThunk(
  `${EntityStoreName.Tournaments}/markAsReady`,
  (data: MarkUserAsReadyRequest): Promise<TournamentEntity> => {
    return apiRequestCall<TournamentEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Tournaments,
        urlPrefix: `${data.tournamentId}/games/${data.payload.gameId}/join`,
      },
    });
  }
);

export const updateTrackedBy = createAsyncThunk(
  `${EntityStoreName.Tournaments}/tracked-by`,
  (id: Identifier): Promise<TournamentEntity> => {
    return apiRequestCall<TournamentEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Tournaments,
        urlPrefix: `${id}/tracked-by`,
      },
    });
  }
);

export const registerReducers = (
  builder: ActionReducerMapBuilder<CurrentTournamentState>
): void => {
  builder
    .addCase(getTournamentById.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getTournamentById.fulfilled, (state, action) => {
      const teams =
        action?.payload?.teams?.map((team, index) => ({ ...team, index: index + 1 })) ?? [];
      state.entity = { ...state.entity, ...action.payload, teams };
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getTournamentById.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(cancelTournament.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.Canceling,
        action.meta.requestId
      );
    })
    .addCase(cancelTournament.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Canceling,
        action.meta.requestId
      );
    })
    .addCase(cancelTournament.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Canceling,
        action.meta.requestId
      );
    })
    .addCase(joinTournament.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.Joining,
        action.meta.requestId
      );
    })
    .addCase(joinTournament.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Joining,
        action.meta.requestId
      );
    })
    .addCase(joinTournament.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Joining,
        action.meta.requestId
      );
    })
    .addCase(getTournamentGameUrl.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getTournamentGameUrl.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getTournamentGameUrl.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(updateTournament.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.ViewingAnimation,
        action.meta.requestId
      );
    })
    .addCase(updateTournament.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.ViewingAnimation,
        action.meta.requestId
      );
    })
    .addCase(updateTournament.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.ViewingAnimation,
        action.meta.requestId
      );
    })
    .addCase(updateInvitationStatus.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingInvitationStatus,
        action.meta.requestId
      );
    })
    .addCase(updateInvitationStatus.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingInvitationStatus,
        action.meta.requestId
      );
    })
    .addCase(updateInvitationStatus.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingInvitationStatus,
        action.meta.requestId
      );
    })
    .addCase(withdrawTournament.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.Withdrawing,
        action.meta.requestId
      );
    })
    .addCase(withdrawTournament.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Withdrawing,
        action.meta.requestId
      );
    })
    .addCase(withdrawTournament.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Withdrawing,
        action.meta.requestId
      );
    })
    .addCase(disqualifyTeam.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.Disqualification,
        action.meta.requestId
      );
    })
    .addCase(disqualifyTeam.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Disqualification,
        action.meta.requestId
      );
    })
    .addCase(disqualifyTeam.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Disqualification,
        action.meta.requestId
      );
    })
    .addCase(updateMatchGameResults.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingMatchGameResults,
        action.meta.requestId
      );
    })
    .addCase(updateMatchGameResults.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingMatchGameResults,
        action.meta.requestId
      );
    })
    .addCase(updateMatchGameResults.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingMatchGameResults,
        action.meta.requestId
      );
    })
    .addCase(updateSpectatorStatus.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingSpectatorStatus,
        action.meta.requestId
      );
    })
    .addCase(updateSpectatorStatus.fulfilled, (state, action) => {
      const spectators = (action.payload as LolTournamentEntity).spectators;
      state.entity = { ...state.entity, ...action.payload, spectators };
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingSpectatorStatus,
        action.meta.requestId
      );
    })
    .addCase(updateSpectatorStatus.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingSpectatorStatus,
        action.meta.requestId
      );
    })
    .addCase(updateTournamentAssets.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingAssets,
        action.meta.requestId
      );
    })
    .addCase(updateTournamentAssets.fulfilled, (state, action) => {
      state.entity.cardImageKey = action.payload.cardImageKey;
      state.entity.bannerImageKey = action.payload.bannerImageKey;

      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingAssets,
        action.meta.requestId
      );
    })
    .addCase(updateTournamentAssets.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.UpdatingAssets,
        action.meta.requestId
      );
    })
    .addCase(markAsReady.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        CurrentTournamentStoreStatus.Joining,
        action.meta.requestId
      );
    })
    .addCase(markAsReady.fulfilled, (state, action) => {
      const teams =
        action?.payload?.teams?.map((team, index) => ({ ...team, index: index + 1 })) ?? [];
      state.entity = { ...state.entity, ...action.payload, teams };
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Joining,
        action.meta.requestId
      );
    })
    .addCase(markAsReady.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        CurrentTournamentStoreStatus.Joining,
        action.meta.requestId
      );
    })
    .addCase(runBracketMatch.pending, (state, action) => {
      const status = CurrentTournamentStoreStatus.RunBracketMatch;
      entityStoreStatus.addRequestId(state, status, action.meta.requestId);
    })
    .addCase(runBracketMatch.fulfilled, (state, action) => {
      const status = CurrentTournamentStoreStatus.RunBracketMatch;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(runBracketMatch.rejected, (state, action) => {
      const status = CurrentTournamentStoreStatus.RunBracketMatch;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(updateTrackedBy.pending, (state, action) => {
      const status = CurrentTournamentStoreStatus.UpdatingTrackedBy;
      entityStoreStatus.addRequestId(state, status, action.meta.requestId);
    })
    .addCase(updateTrackedBy.fulfilled, (state, action) => {
      const status = CurrentTournamentStoreStatus.UpdatingTrackedBy;
      const trackedBy = action?.payload?.trackedBy ?? [];
      state.entity = { ...state.entity, trackedBy };
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(updateTrackedBy.rejected, (state, action) => {
      const status = CurrentTournamentStoreStatus.UpdatingTrackedBy;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    });
};
